<template>
  <div class="fmp">
    <div v-for="group in groups" :key="group.key" class="fmp-module"
         :class="{ 'is-open': open[group.key] }">
      <div class="fmp-head">
        <button type="button" class="fmp-toggle" :aria-expanded="open[group.key] ? 'true' : 'false'"
                @click="toggleSection(group.key)">
          <span class="fmp-title">{{ group.name }}</span>
          <ion-icon class="fmp-chevron" name="chevron-down-outline"></ion-icon>
          <span class="fmp-count">{{ countOf(group) }}/{{ group.features.length }}</span>
        </button>
        <button type="button" class="fmp-all" @click="onToggleModule(group)">
          {{ isFull(group) ? 'Desmarcar todas' : 'Selecionar todas' }}
        </button>
      </div>

      <div v-show="open[group.key]" class="fmp-grid">
        <div v-for="feature in group.features" :key="feature.value"
             class="fmp-pill" :class="{ 'is-on': isOn(feature.value) }"
             :title="feature.notes || ''">
          <div class="checkbox inline-block">
            <input type="checkbox" :id="'fmp-' + feature.value"
                   :checked="isOn(feature.value)" @change="onToggle(feature.value)">
            <label @click="onToggle(feature.value)">{{ feature.label }}</label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  groupByModule, initialOpenState, isModuleFullySelected, selectedCount, toggleModule,
} from "./featureModules";

/**
 * Seletor de funcionalidades agrupado por modulo: os seis modulos da secao
 * "Funcionalidades" de /detalhes-da-empresa, com CHECKBOX no lugar do switch.
 *
 * La o switch liga e desliga NA HORA, uma empresa por vez; aqui nada acontece
 * ate o botao da tela, entao o controle certo e o de selecao, nao o de estado.
 *
 * Usado pela Liberacao (featureWindowCreate.vue, valor = enum) e pelo modal de
 * funcionalidades do boleto/venda (modalFeatureType.vue, valor = id do banco).
 * companyDetail.vue ainda repete o mesmo bloco seis vezes na propria secao
 * "Funcionalidades": mexer nessa tela esta fora desta entrega.
 */
export default {
  name: "feature-module-picker",
  props: {
    options: { type: Array, default: () => [] },
    value: { type: Array, default: () => [] },
  },
  data() {
    // Secao aberta/fechada por chave de modulo: { journey: true, personalDepartment: false, ... }
    return { open: {} };
  },
  computed: {
    groups() {
      return groupByModule(this.options);
    },
  },
  watch: {
    // As opcoes chegam da API depois do mount. So preenche chave que ainda nao
    // existe: recarregar as opcoes nao pode fechar o que o operador abriu.
    groups: {
      immediate: true,
      handler(groups) {
        const initial = initialOpenState(groups);
        const hasAny = Object.keys(this.open).length > 0;
        Object.keys(initial).forEach((key) => {
          if (!(key in this.open)) this.$set(this.open, key, hasAny ? false : initial[key]);
        });
      },
    },
  },
  methods: {
    toggleSection(key) {
      this.$set(this.open, key, !this.open[key]);
    },
    countOf(group) {
      return selectedCount(this.value, group);
    },
    isOn(featureType) {
      return this.value.indexOf(featureType) !== -1;
    },
    isFull(group) {
      return isModuleFullySelected(this.value, group);
    },
    onToggle(featureType) {
      const next = this.isOn(featureType)
        ? this.value.filter((v) => v !== featureType)
        : this.value.concat([featureType]);
      this.$emit('input', next);
    },
    onToggleModule(group) {
      this.$emit('input', toggleModule(this.value, group));
    },
  },
};
</script>

<style scoped>
.fmp-module {
  margin-bottom: 12px;
  border: 1px solid #E6E9EC;
  border-radius: 8px;
  padding: 0 16px;
}
.fmp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
}
.fmp-module.is-open .fmp-head { border-bottom: 1px solid #E6E9EC; }

/* A tela de liberacao renderiza este componente dentro de .filters, e o
   custom.css trata TODO `.filters button` como botao azul: fundo, 120x33px,
   top: 4px, margin-left: 17px — e `.filters button:hover` escurece o fundo, que
   sob o texto roxo ficava ilegivel. Os dois botoes daqui sao texto e desfazem
   tudo isso, inclusive no hover/focus (la o seletor ganha de uma classe so). */
.fmp-toggle,
.fmp-all,
.fmp-toggle:hover,
.fmp-toggle:focus,
.fmp-all:hover,
.fmp-all:focus {
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  position: static;
  width: auto;
  height: auto;
  cursor: pointer;
}
.fmp-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  color: inherit;
  text-align: left;
}
.fmp-title { font-weight: 600; font-size: 18px; }
.fmp-chevron { font-size: 18px; transition: transform .15s ease; }
.fmp-module.is-open .fmp-chevron { transform: rotate(180deg); }
.fmp-count { font-size: 12px; color: #6B7785; }
.fmp-all { font-size: 12px; color: #2F23D6; }
.fmp-all:hover,
.fmp-all:focus { color: #1F16A3; text-decoration: underline; }

/* Quantas colunas couberem, cada uma com pelo menos 200px: a mesma grade serve
   a tela da Liberacao e o modal de 900px sem quebrar para fora. */
.fmp-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin: 16px 0;
}
.fmp-pill {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
  padding: 8.5px 12px;
  border: 1px solid #E6E9EC;
  border-radius: 18px;
}
.fmp-pill.is-on { border-color: #2F23D6; }
.fmp-pill .checkbox { position: relative; min-width: 0; margin: 0; padding: 0; }

/* A caixa que se ve NAO e o input (opacity: 0 no custom.css): e o ::before do
   label, 20x20 puxado -20px para a esquerda. Aqui ela fica com 16px, ancorada
   no inicio do label, e o texto comeca 10px depois dela. */
.fmp-pill .checkbox label {
  display: block;
  margin: 0;
  padding: 0 0 0 26px;
  min-height: 16px;
  line-height: 16px;
  overflow-wrap: anywhere;
  cursor: pointer;
}
.fmp-pill .checkbox label::before,
.fmp-pill .checkbox label::after {
  width: 16px;
  height: 16px;
  top: 0;
  left: 0;
  margin-left: 0;
}
.fmp-pill .checkbox label::after { padding: 0 0 0 2px; font-size: 11px; line-height: 16px; }
.fmp-pill .checkbox input[type=checkbox] {
  position: absolute;
  top: 0;
  left: 0;
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/default/featureModulePicker.vue